<script setup lang="ts">
import { DialogModal, SwitchComponent } from '@metapic-apps/shared'
import { type FormInstance, type FormRules } from 'element-plus'
import { computed, reactive, ref, watch } from 'vue'

import { keywordRule, urlRule } from './validators'

import { useCreateUrlMatchingRule, useEditUrlMatchingRule } from '@/api'
import { notification } from '@/lib/helpers'
import { cleanUpUrl } from '@/lib/helpers/query.helpers'
import type { UrlMatchingRulePayloadType, UrlMatchingRuleType } from '@/lib/types/url-matching-rules.types'
import {
  BoxComponent,
  ButtonComponent,
  FlexComponent,
  InputComponent,
  RadioGroupComponent,
  SelectComponent,
  TitleComponent,
  TypographyComponent,
} from '@/ui-library'

const props = defineProps<{
  advertiserId: number
}>()
const emits = defineEmits(['handleSubmitSuccess'])
const isOpen = defineModel<boolean>('modelValue', { default: false })
const rule = defineModel<UrlMatchingRuleType | null>('rule', { default: null })
const { createUrlMatchingRule, isLoading: isCreating, error: createError } = useCreateUrlMatchingRule()
const { editUrlMatchingRule, isLoading: isEditing, error: editError } = useEditUrlMatchingRule()
const isLoading = computed(() => isCreating.value || isEditing.value)
const ruleFormRef = ref<FormInstance>()
const formData = reactive<UrlMatchingRulePayloadType>({
  type: rule.value?.type || 'allow',
  url: rule.value?.url || '',
  keyword: rule.value?.keyword || '',
  isMain: rule.value?.isMain ?? false,
})
const options = [
  { label: 'Allow', value: 'allow' },
  { label: 'Block', value: 'block' },
]
const blockTypeValue = ref(rule.value?.type === 'block' ? (rule.value?.url ? 'url' : 'keyword') : 'url')
const blockTypeOptions = [
  { label: 'URL', value: 'url' },
  { label: 'Keyword', value: 'keyword' },
]
const rules = reactive<FormRules>({
  url: [
    {
      validator: (_rule, value, callback) => {
        if (formData.type === 'allow' || (formData.type === 'block' && blockTypeValue.value === 'url')) {
          if (!value) {
            return callback(new Error('URL is required'))
          }
        }
        callback()
      },
      trigger: 'blur',
    },
    urlRule,
    { max: 200, message: 'Field must be shorter than 200 symbols', trigger: 'blur' },
  ],
  keyword: [
    {
      validator: (_rule, value, callback) => {
        if (formData.type === 'block' && blockTypeValue.value === 'keyword') {
          if (!value) {
            return callback(new Error('Keyword is required'))
          }
        }
        callback()
      },
      trigger: 'blur',
    },
    keywordRule,
    { max: 30, message: 'Field must be shorter than 30 symbols', trigger: 'blur' },
  ],
})

const sanitizeUrl = (val: string) => {
  if (!val) {
    return
  }
  const sanitized = cleanUpUrl(val.replace(/^(https?:\/\/)+/, ''))
  if (formData.url !== sanitized) {
    formData.url = sanitized
  }
}

const serializeData = (data: UrlMatchingRulePayloadType) => {
  const { type, url, keyword } = data
  if (type === 'block') {
    return {
      ...data,
      isMain: false,
      url: blockTypeValue.value === 'url' ? url : undefined,
      keyword: blockTypeValue.value === 'keyword' ? keyword : undefined,
    }
  }
  return {
    ...data,
    url,
    keyword: keyword || undefined,
  }
}

const submitForm = async () => {
  await ruleFormRef.value?.validate(async (valid) => {
    if (valid) {
      const data = serializeData(formData)
      if (rule.value) {
        await editRule(data, rule.value.id)
      } else {
        await createRule(data)
      }
    }
  })
}

const createRule = async (data: UrlMatchingRulePayloadType) => {
  await createUrlMatchingRule(props.advertiserId, data)
  if (!createError.value) {
    emits('handleSubmitSuccess')
    closeForm()
    notification.success('The url matching rule was created successfully')
  }
}

const editRule = async (data: UrlMatchingRulePayloadType, ruleId: number) => {
  await editUrlMatchingRule(props.advertiserId, ruleId, data)
  if (!editError.value) {
    emits('handleSubmitSuccess')
    closeForm()
    notification.success('The url matching rule was updated successfully')
  }
}

const resetValidation = () => {
  ruleFormRef.value?.clearValidate()
}

const closeForm = () => {
  ruleFormRef.value?.resetFields()
  isOpen.value = false
  rule.value = null
}

watch(
  () => formData.type,
  (newType) => {
    if (newType === rule.value?.type) {
      formData.url = rule.value?.url || ''
      formData.keyword = rule.value?.keyword || ''
      formData.isMain = rule.value?.isMain ?? true
    } else {
      formData.url = ''
      formData.keyword = ''
      formData.isMain = true
    }
  },
)
</script>
<template>
  <DialogModal v-model="isOpen" :destroy-on-close="true" @before-close="closeForm">
    <template #header>
      <BoxComponent :mb="24">
        <TypographyComponent variant="headline-medium">{{ rule ? 'Edit URL rule' : 'Add new URL rule' }}</TypographyComponent>
      </BoxComponent>
    </template>
    <el-form ref="ruleFormRef" :model="formData" :rules="rules" hide-required-asterisk :scroll-to-error="true" label-position="top">
      <FlexComponent direction="column" :gap="24">
        <el-form-item prop="type">
          <SelectComponent v-model="formData.type" title="Action" name="type" :options="options" :teleported="false" />
        </el-form-item>
        <template v-if="formData.type === 'allow'">
          <el-form-item prop="url">
            <InputComponent v-model="formData.url" name="url" placeholder="e.g. example.com/path" @change="sanitizeUrl">
              <template #header>
                <BoxComponent :mb="8">
                  <TitleComponent title="URL">
                    <template #tooltip>
                      <BoxComponent :w="280">
                        <BoxComponent :mb="4">
                          <TypographyComponent variant="title-medium">URL</TypographyComponent>
                        </BoxComponent>
                        <TypographyComponent variant="body-medium">
                          URL The base URL for the advertiser, e.g. asos.com, asos.com/dresses, or a subdomain like de.asos.com.
                        </TypographyComponent>
                      </BoxComponent>
                    </template>
                  </TitleComponent>
                </BoxComponent>
              </template>
            </InputComponent>
          </el-form-item>
          <el-form-item prop="keyword">
            <InputComponent v-model="formData.keyword" name="keyword" placeholder="e.g. shoe or dress">
              <template #header>
                <BoxComponent :mb="8">
                  <TitleComponent>
                    <template #title>
                      <TypographyComponent variant="title-large">Keyword</TypographyComponent>
                      <TypographyComponent variant="body-large"> (optional) </TypographyComponent>
                    </template>
                    <template #tooltip>
                      <BoxComponent :w="280">
                        <BoxComponent :mb="4">
                          <TypographyComponent variant="title-medium">Keyword</TypographyComponent>
                        </BoxComponent>
                        <TypographyComponent variant="body-medium">
                          A word that must be in the URL, e.g. "Lego" for all Lego products from the advertiser.
                        </TypographyComponent>
                      </BoxComponent>
                    </template>
                  </TitleComponent>
                </BoxComponent>
              </template>
            </InputComponent>
          </el-form-item>
          <el-form-item prop="isMain">
            <SwitchComponent
              v-model="formData.isMain"
              name="isMain"
              title="Set as main URL (creators will be redirected to main URL when clicking the advertiser card)"
            />
          </el-form-item>
        </template>
        <template v-else>
          <RadioGroupComponent v-model="blockTypeValue" name="blockTypeValue" :options="blockTypeOptions" @change="resetValidation">
            <template #option="{ option }">
              <TitleComponent v-if="option.value === 'url'" title="URL">
                <template #tooltip>
                  <BoxComponent :w="280">
                    <BoxComponent :mb="4">
                      <TypographyComponent variant="title-medium">URL</TypographyComponent>
                    </BoxComponent>
                    <TypographyComponent variant="body-medium">
                      URL The base URL for the advertiser, e.g. asos.com, asos.com/dresses, or a subdomain like de.asos.com.
                    </TypographyComponent>
                  </BoxComponent>
                </template>
              </TitleComponent>
              <TitleComponent v-else title="Keyword">
                <template #tooltip>
                  <BoxComponent :w="280">
                    <BoxComponent :mb="4">
                      <TypographyComponent variant="title-medium">Keyword</TypographyComponent>
                    </BoxComponent>
                    <TypographyComponent variant="body-medium">
                      A word that must be in the URL, e.g. "Lego" for all Lego products from the advertiser.
                    </TypographyComponent>
                  </BoxComponent>
                </template>
              </TitleComponent>
            </template>
          </RadioGroupComponent>
          <el-form-item :prop="blockTypeValue">
            <InputComponent
              v-if="blockTypeValue === 'url'"
              v-model="formData.url"
              name="url"
              placeholder="e.g. example.com/path"
              @change="sanitizeUrl"
            />
            <InputComponent v-else v-model="formData.keyword" name="keyword" placeholder="e.g. shoe or dress" />
          </el-form-item>
        </template>
        <div>
          <FlexComponent :gap="8" justify="flex-end">
            <ButtonComponent variant="text" :is-disabled="isLoading" @click="closeForm"> Cancel </ButtonComponent>
            <ButtonComponent variant="primary" :is-loading="isLoading" @click.prevent="submitForm">
              {{ rule ? 'Edit rule' : 'Add rule' }}
            </ButtonComponent>
          </FlexComponent>
        </div>
      </FlexComponent>
    </el-form>
  </DialogModal>
</template>
<style lang="scss" scoped></style>
